import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Camera, FolderOpen } from 'lucide-react';
import { IS_NATIVE, NATIVE_PLATFORM } from '../../config/runtime';
import { runBackHandlers, useNativeBack } from '../../utils/nativeBack';
import { installNativeFileShims } from '../../utils/nativeFiles';
import { FILE_SOURCE_EVENT, chooseFileInto, installCameraChooser, takePhotoInto } from '../../utils/nativeCamera';
import { installPushListeners, registerPush } from '../../utils/nativePush';

// Before any page can build a download or open an upload: the patches have to
// be in place by the time the first button is pressed. A no-op in a browser.
if (IS_NATIVE) {
  installNativeFileShims();
  installCameraChooser();
}

// Anything Radix keeps open as a layer: dialogs, sheets, alert dialogs,
// dropdowns, selects, popovers. Escape is how each of them closes itself, so
// the back button sends Escape rather than knowing about any one of them.
const OPEN_LAYER = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[data-radix-popper-content-wrapper]',
].join(',');

function closeTopLayer() {
  if (!document.querySelector(OPEN_LAYER)) return false;
  const target = document.activeElement || document.body;
  target.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true, cancelable: true }));
  return true;
}

/**
 * The native half of the app that has no screen of its own. Mounted once,
 * inside the router, only when running as the store app.
 *
 * Android's back button walks back out of whatever is on top before it
 * leaves a page: an open dialog or menu first, then anything a screen
 * registered (the navigation drawer), then the page history — and on the
 * first page it puts the app in the background rather than killing it, so a
 * half-keyed entry is still there when the operator comes back.
 */
export default function NativeShell() {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [fileInput, setFileInput] = useState(null);

  useNativeBack(Boolean(fileInput), () => setFileInput(null));

  useEffect(() => {
    SplashScreen.hide().catch(() => {});
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    if (NATIVE_PLATFORM === 'android') {
      StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});
    }

    const sub = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (closeTopLayer()) return;
      if (runBackHandlers()) return;
      if (window.location.pathname !== '/') {
        // The WebView can report no history even on a deep page — after a
        // relaunch onto a restored route, or for entries Chromium skips because
        // they were added without a tap. Away from Home, back must never leave
        // the app: it goes back if it can, and Home if it cannot.
        if (canGoBack) window.history.back();
        else navigateRef.current('/', { replace: true });
        return;
      }
      CapApp.minimizeApp().catch(() => {});
    });
    return () => { sub.then((h) => h.remove()).catch(() => {}); };
  }, []);

  // Push: listeners once; registration for a restored session now and for
  // every fresh sign-in (AuthContext announces those as `tokenChanged`).
  useEffect(() => {
    installPushListeners(navigate);
    registerPush();
    const onLogin = () => { registerPush(); };
    window.addEventListener('tokenChanged', onLogin);
    return () => window.removeEventListener('tokenChanged', onLogin);
  }, [navigate]);

  useEffect(() => {
    const onPick = (e) => setFileInput(e.detail?.input || null);
    window.addEventListener(FILE_SOURCE_EVENT, onPick);
    return () => window.removeEventListener(FILE_SOURCE_EVENT, onPick);
  }, []);

  if (!fileInput) return null;

  const pick = (fn) => {
    const input = fileInput;
    setFileInput(null);
    fn(input);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end bg-slate-900/40" onClick={() => setFileInput(null)}>
      <div
        className="w-full rounded-t-2xl bg-white p-3"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Add a file"
      >
        <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-slate-200" />
        <button type="button" onClick={() => pick(takePhotoInto)} className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-base font-medium text-slate-800 active:bg-slate-100">
          <Camera className="h-5 w-5 text-blue-600" /> Take photo
        </button>
        <button type="button" onClick={() => pick(chooseFileInto)} className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-base font-medium text-slate-800 active:bg-slate-100">
          <FolderOpen className="h-5 w-5 text-blue-600" /> Choose a file or photo
        </button>
        <button type="button" onClick={() => setFileInput(null)} className="mt-1 w-full rounded-xl px-4 py-3 text-base text-slate-500 active:bg-slate-100">
          Cancel
        </button>
      </div>
    </div>
  );
}
